import { AppSettings } from "@/AppSettings";
import { CalculateConfigurationTrialParticipantSourceDataAllocations, CalculateParticipantSourceDataAllocationsResponse, ConfigurationSourceData, ConfigurationSourceDataAllocation, ConfigurationStatus, ConfigurationTrial as ConfigurationTrialDto, ParticipantSourceDataAllocationUpdate, SaveConfigurationTrial, SourceDataAllocationType, Trial } from "@/api/dtos";
import Form from "@/components/forms/Form";
import InputOnlyField from "@/components/forms/fields/inputOnly/InputOnlyField";
import Checkbox from "@/components/forms/inputs/Checkbox";
import Select, { ISelectItem } from "@/components/forms/inputs/Select";
import Text from "@/components/forms/inputs/Text";
import IsLoading from "@/components/layouts/IsLoading";
import SetLinks from "@/components/layouts/SetLinks";
import Show from "@/components/layouts/Show";
import CoordinatingGroupsContext from "@/contexts/data/CoordinatingGroupsContext";
import InstitutionsContext from "@/contexts/data/InstitutionsContext";
import MasterGroupsContext from "@/contexts/data/MasterGroupsContext";
import TrialContext from "@/contexts/data/TrialContext";
import { ConfigurationInstitutionCreateLink, ConfigurationTrialCreateLink, ConfigurationTrialInstitutionCreateLink, ConfigurationTrialTrialCreateLink, NotesAndQueriesInstitutionCreateLink, NotesAndQueriesTrialCreateLink } from "@/contexts/layouts/LinkContext";
import { FormError } from "@/errors";
import useConfigurationSourceDataAllocations from "@/hooks/data/useConfigurationSourceDataAllocations";
import useConfigurationSourceDatas from "@/hooks/data/useConfigurationSourceDatas";
import useConfigurationTrial from "@/hooks/data/useConfigurationTrial";
import useTrialNotesAndQueries from "@/hooks/data/useTrialNotesAndQueries";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Divider, InputAdornment, List, ListItemButton, ListItemIcon, ListItemText, Paper, Typography, darken, lighten, styled, FormControlLabelProps, Button, Box, buttonClasses } from '@mui/material';
import { grey } from "@mui/material/colors";
import { Condition, ConditionMode, FormManager, IConditionProps, IFormManagerOptions, IFormOptions, IFormProps, IFormState, IFormSubmit, IFormSubmitFailed, IFormSubmitValidationFailed, IFormValidate, IOnConditionNotMet, ValidateOn, useScopedField } from "@ngt/forms-core";
import { FC, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import ordinal from 'ordinal'
import FormActionButton from "@/components/forms/FormActionButton";
import FormSubmitButton from "@/components/forms/FormSubmitButton";
import Markdown from "@/components/forms/inputs/Markdown";
import ServiceStackClientContext from "@/contexts/api/ServiceStackClientContext";
import ParticipantSourceDataAllocationSelectionDialog, { IParticipantSourceDataAllocationSelectionForm } from "@/components/dialogs/ParticipantSourceDataAllocationSelectionDialog";
import useParticipants from "@/hooks/data/useParticipants";
import { useSnackbar } from "notistack";
import FormErrorType from "@/types/form/FormErrorType";
import { JsonServiceClient } from "@servicestack/client";
import ParticipantsContext from "@/contexts/data/ParticipantsContext";
import AlertMarkdown from "../inputs/AlertMarkdown";



interface IForm<TConfiguration extends object = any> {
    configuration: TConfiguration;
    allocations: ConfigurationSourceDataAllocation[];
}

const constantAllocationCondition: IConditionProps<IForm, FormError>['condition'] = (state, parentPropertyPath) => {
    return state.value?.configuration?.allocationType === SourceDataAllocationType.Constant
}

const repeatingAllocationCondition: IConditionProps<IForm, FormError>['condition'] = (state, parentPropertyPath) => {
    return state.value?.configuration?.allocationType === SourceDataAllocationType.RepeatingCycle
}

const firstXAllocationCondition: IConditionProps<IForm, FormError>['condition'] = (state, parentPropertyPath) => {
    return state.value?.configuration?.allocationType === SourceDataAllocationType.FirstXParticipants
}

const firstXAndrepeatingAllocationCondition: IConditionProps<IForm, FormError>['condition'] = (state, parentPropertyPath) => {
    return state.value?.configuration?.allocationType === SourceDataAllocationType.FirstXParticipantsAndRepeatingCycle
}

const FormContainer = styled(Paper)(({ theme }) => ({ 
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
}));

const OriginalName = styled(InputAdornment)(({ theme }) => ({
    background: theme.palette.mode === 'dark' ? darken(grey[600], 0.5): lighten(grey[600], 0.5),
    alignSelf: 'stretch',
    maxHeight: 'unset',
    height: 'unset',
    padding: theme.spacing(0, 2, 0, 2),
    marginRight: theme.spacing(-1.75),
    borderTopRightRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    minWidth: 400
}));

const AllocationDelete = styled(InputAdornment)(({ theme }) => ({
    alignSelf: 'stretch',
    alignItems: 'stretch',
    maxHeight: 'unset',
    height: 'unset',
    marginRight: theme.spacing(0),
    borderTopRightRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
    justifyContent: 'stretch',

    [`& .${buttonClasses.root}`]: {
        minWidth: theme.spacing(0),
        padding: theme.spacing(2)
    },
}));


const AddButton = styled(Button)(({ theme }) => ({
    minWidth: theme.spacing(6),
    minHeight: theme.spacing(6), 
    padding: theme.spacing(0),
    marginLeft: 'auto', 
    display: 'flex'
}));


const FormButtons = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end'

}));

interface IContextQuery {
    trialId?: number;
    masterGroupId?: number;
    coordinatingGroupId?: number;
    institutionId?: number;
    participantId?: number;
}

interface IAllocationComponent {
    sourceDataOptions?: ISelectItem<number>[]; 
    contextQuery?: IContextQuery;
}

const ConfigurationTrialConstant: FC<IAllocationComponent> = ({
    sourceDataOptions
}) => {

    return (
        <InputOnlyField
            propertyPath="allocations[0].configurationSourceDataId"
            label="Constant Allocation"
            helperText="Select the allocation that will be received by all participants"
            FormControlProps={{
                margin: 'normal'
            }}
            component={Select}
            options={sourceDataOptions}
        />
    )
}

const ConfigurationTrialFirstX: FC<IAllocationComponent> = ({
    sourceDataOptions,
    contextQuery
}) => {
    const { state: { value: allocations }, context: {setValue: setAllocations}  } = useScopedField<ConfigurationSourceDataAllocation[]>('allocations', {value: true})

    const allocationSelects = useMemo(() => {
        if ((allocations?.length ?? 0) <= 1) {
            return [];
        }
        return allocations?.filter((_, i) => i !== 0).map((_, i) => {
            return (
                <InputOnlyField
                    key={i}
                    propertyPath={`allocations[${i + 1}].configurationSourceDataId`}
                    label={`${ordinal(i + 1)} Participant Allocation`}
                    helperText={`Select the allocation that will be received by the ${ordinal(i + 1)} participant at each institution.`}
                    FormControlProps={{
                        margin: 'normal'
                    }}
                    component={Select}
                    options={sourceDataOptions}
                    endAdornment={
                        allocations.length > 2 && (
                            <AllocationDelete position="end">
                                <Button
                                    color="error"
                                    variant="contained"
                                    onClick={() => setAllocations((current) => {
                                        return current.filter((_, ci) => ci !== (i + 1))
                                    })}
                                >
                                    <FontAwesomeIcon icon={AppSettings.icons.delete} size="lg" fixedWidth />
                                </Button>
                            </AllocationDelete>
                        )
                    }
                />
            )
        })
    }, [allocations?.length, sourceDataOptions, setAllocations]);

    return (
        <>
            <InputOnlyField
                propertyPath="allocations[0].configurationSourceDataId"
                label="Constant Allocation"
                helperText={`Select the allocation that will be received by all participants except the first ${(allocations?.length ?? 1) - 1} at each institution.`}
                FormControlProps={{
                    margin: 'normal'
                }}
                component={Select}
                options={sourceDataOptions}
            />
            {allocationSelects}
            <AddButton
                variant="contained"
                color="primary"
                onClick={() => {
                    setAllocations((current) => {
                        return [...current, new ConfigurationSourceDataAllocation(contextQuery)]
                    })
                }}
            >
                <FontAwesomeIcon icon={AppSettings.icons.add} size="lg" fixedWidth />
            </AddButton>
        </>
    )
}

const ConfigurationTrialRepeatingCycle: FC<IAllocationComponent> = ({
    sourceDataOptions,
    contextQuery
}) => {
    const { state: { value: allocations }, context: {setValue: setAllocations}  } = useScopedField<ConfigurationSourceDataAllocation[]>('allocations', {value: true})

    const allocationSelects = useMemo(() => {
        if ((allocations?.length ?? 0) < 1) {
            return [];
        }
        return allocations?.map((_, i) => {
            return (
                <InputOnlyField
                    key={i}
                    propertyPath={`allocations[${i}].configurationSourceDataId`}
                    label={`${ordinal(i + 1)} Participant Allocation`}
                    helperText={`Select the allocation that will be received by the ${ordinal(i + 1)} participant in each repetition at each institution.`}
                    FormControlProps={{
                        margin: 'normal'
                    }}
                    component={Select}
                    options={sourceDataOptions}
                    endAdornment={
                        allocations.length > 1 && (
                            <AllocationDelete position="end">
                                <Button
                                    color="error"
                                    variant="contained"
                                    onClick={() => setAllocations((current) => {
                                        return current.filter((_, ci) => ci !== i)
                                    })}
                                >
                                    <FontAwesomeIcon icon={AppSettings.icons.delete} size="lg" fixedWidth />
                                </Button>
                            </AllocationDelete>
                        )
                    }
                />
            )
        })
    }, [allocations?.length, sourceDataOptions, setAllocations]);

    return (
        <>
            {allocationSelects}
            <AddButton
                variant="contained"
                color="primary"
                onClick={() => {
                    setAllocations((current) => {
                        return [...current, new ConfigurationSourceDataAllocation(contextQuery)]
                    })
                }}
            >
                <FontAwesomeIcon icon={AppSettings.icons.add} size="lg" fixedWidth />
            </AddButton>
        </>
    )
}

const ConfigurationTrialFirstXAndRepeatingCycle: FC<IAllocationComponent> = ({
    sourceDataOptions,
    contextQuery
}) => {
    const { state: { value: allocations }, context: {setValue: setAllocations}  } = useScopedField<ConfigurationSourceDataAllocation[]>('allocations', {value: true})

    const allocationSelects = useMemo(() => {
        if ((allocations?.length ?? 0) < 1) {
            return {
                firstX: [],
                repeating: []
            };
        }

        let totalFirstX = allocations?.filter(x => x.type === SourceDataAllocationType.FirstXParticipants).length ?? 0;
        let totalRepeating = allocations?.filter(x => x.type === SourceDataAllocationType.RepeatingCycle).length ?? 0;

        let firstXCount = 0;
        let repeatingCount = 0;

        return {
            firstX: allocations?.map((x, i) => {
                if (x.type !== SourceDataAllocationType.FirstXParticipants) {
                    return null;
                }

                firstXCount++;

                return (
                    <InputOnlyField
                        key={i}
                        propertyPath={`allocations[${i}].configurationSourceDataId`}
                        label={`${ordinal(firstXCount)} Participant Allocation`}
                        helperText={`Select the allocation that will be received by the ${ordinal(firstXCount)} participant at each institution.`}
                        FormControlProps={{
                            margin: 'normal'
                        }}
                        component={Select}
                        options={sourceDataOptions}
                        endAdornment={
                            totalFirstX > 1 && (
                                <AllocationDelete position="end">
                                    <Button
                                        color="error"
                                        variant="contained"
                                        onClick={() => setAllocations((current) => {
                                            return current.filter((_, ci) => ci !== i)
                                        })}
                                    >
                                        <FontAwesomeIcon icon={AppSettings.icons.delete} size="lg" fixedWidth />
                                    </Button>
                                </AllocationDelete>
                            )
                        }
                    />
                )
            }).filter(x => !!x),
            repeating:  allocations?.map((x, i) => {
                if (x.type !== SourceDataAllocationType.RepeatingCycle) {
                    return null;
                }

                repeatingCount++;

                return (
                    <InputOnlyField
                        key={i}
                        propertyPath={`allocations[${i}].configurationSourceDataId`}
                        label={`${ordinal(repeatingCount)} Participant Allocation`}
                        helperText={`Select the allocation that will be received by the ${ordinal(repeatingCount)} participant in each repetition at each institution.`}
                        FormControlProps={{
                            margin: 'normal'
                        }}
                        component={Select}
                        options={sourceDataOptions}
                        endAdornment={
                            totalRepeating > 1 && (
                                <AllocationDelete position="end">
                                    <Button
                                        color="error"
                                        variant="contained"
                                        onClick={() => setAllocations((current) => {
                                            return current.filter((_, ci) => ci !== i)
                                        })}
                                    >
                                        <FontAwesomeIcon icon={AppSettings.icons.delete} size="lg" fixedWidth />
                                    </Button>
                                </AllocationDelete>
                            )
                        }
                    />
                )
            }).filter(x => !!x)
        };
    }, [allocations?.length, sourceDataOptions, setAllocations]);

    return (
        <>
            <Typography variant="h3" color="secondary.main">First X</Typography>
            {allocationSelects.firstX}
            <AddButton
                variant="contained"
                color="primary"
                onClick={() => {
                    setAllocations((current) => {
                        return [...current, new ConfigurationSourceDataAllocation({...contextQuery, type: SourceDataAllocationType.FirstXParticipants})]
                    })
                }}
            >
                <FontAwesomeIcon icon={AppSettings.icons.add} size="lg" fixedWidth />
            </AddButton>
            <Typography variant="h3" color="secondary.main">Repeating Cycle</Typography>
            {allocationSelects.repeating}
            <AddButton
                variant="contained"
                color="primary"
                onClick={() => {
                    setAllocations((current) => {
                        return [...current, new ConfigurationSourceDataAllocation({...contextQuery, type: SourceDataAllocationType.RepeatingCycle})]
                    })
                }}
            >
                <FontAwesomeIcon icon={AppSettings.icons.add} size="lg" fixedWidth />
            </AddButton>
        </>
    )
}

const onConditionNotMet: IOnConditionNotMet<IForm, FormError> = (propertyPaths, formActions) => {
    

    formActions?.setTouched(touched => {
        propertyPaths.forEach(name => {
            delete touched[name];
        })

        return touched;
    }, true);

    formActions?.setFocused(focused => {
        propertyPaths.forEach(name => {
            delete focused[name];
        });

        return focused;
    }, true);
}

export interface IConfigurationContextTemplateProps<TConfiguration extends object = any> {
    allocationTypes: SourceDataAllocationType[];
    statuses: ConfigurationStatus[];
    entityName: string;
    title?: string;
    contextQuery?: IContextQuery;
    configuration?: TConfiguration;
    configurationLoading?: boolean;
    configurationValidating?: boolean;
    returnUrl?: string;
    children?: ReactNode;
    onSave: (client: JsonServiceClient, data?: TConfiguration, allocations?: ConfigurationSourceDataAllocation[], participantSourceDataAllocationUpdates?: ParticipantSourceDataAllocationUpdate[]) => Promise<any>;
    onCalculateAllocations: (client: JsonServiceClient, contextQuery?: IContextQuery, data?: TConfiguration, allocations?: ConfigurationSourceDataAllocation[] ) => Promise<CalculateParticipantSourceDataAllocationsResponse>;
    onValidate: (client: JsonServiceClient, errors: Record<string, FormError[]>, formState: IFormState<IForm<TConfiguration>, FormError>) => Promise<Record<string, FormError[]>>;
}

const ConfigurationContextTemplate = <TConfiguration extends object = any>({
    contextQuery,
    allocationTypes,
    entityName,
    title,
    statuses,
    configuration,
    returnUrl,
    configurationLoading,
    configurationValidating,
    children,
    onSave,
    onCalculateAllocations,
    onValidate
}: IConfigurationContextTemplateProps<TConfiguration>) => {
    const { enqueueSnackbar } = useSnackbar();
    const client = useContext(ServiceStackClientContext);
    const { data: trial, oid: trialOid } = useContext(TrialContext);    
    const { idMap: institutions } = useContext(InstitutionsContext);
    const { idMap: participants } = useContext(ParticipantsContext);
    const { data: configurationSourceDatas, loading: configurationSourceDatasLoading, validating: configurationSourceDatasValidating } = useConfigurationSourceDatas(trial?.id ? { trialId: trial?.id, isGlobal: false } : undefined, { revalidateOnFocus: false });
    const { data: configurationSourceDataAllocations, loading: configurationSourceDataAllocationsLoading, validating: configurationSourceDataAllocationsValidating } = useConfigurationSourceDataAllocations(contextQuery, { revalidateOnFocus: false });

    const entityNameLowerCase = useMemo(() => entityName.toLowerCase(), [entityName]);

    const [allocationForm, setAllocationForm] = useState<IParticipantSourceDataAllocationSelectionForm<TConfiguration>>()

    const allocationOptions = useMemo(() => {
        const options = [];

        if (allocationTypes.includes(SourceDataAllocationType.Constant))
        {
            options.push({
                id: SourceDataAllocationType.Constant,
                name: 'Constant',
                description: 'Every participant receives the same allocation.'
            })
        }

        if (allocationTypes.includes(SourceDataAllocationType.FirstXParticipants))
        {
            options.push({
                id: SourceDataAllocationType.FirstXParticipants,
                name: 'First X Participants',
                description: 'The first X participants at each institution receive a different allocation from the rest.'
            })
        }

        if (allocationTypes.includes(SourceDataAllocationType.RepeatingCycle))
        {
            options.push({
                id: SourceDataAllocationType.RepeatingCycle,
                name: 'Repeating',
                description: 'A repeating cycle of allocations that is applied to participants per institution.'
            })
        }

        if (allocationTypes.includes(SourceDataAllocationType.FirstXParticipantsAndRepeatingCycle))
        {
            options.push({
                id: SourceDataAllocationType.FirstXParticipantsAndRepeatingCycle,
                name: 'First X Participants Followed By Repeating',
                description: 'The first X participants at each receive a different allocation followed by a repeating cycle of allocations that is applied to participants per institution.'
            })
        }

        if (allocationTypes.includes(SourceDataAllocationType.Inherit))
        {
            options.push({
                id: SourceDataAllocationType.Inherit,
                name: 'Inherit',
                description: 'Inherit the allocation settings from the most relevant parent entity.'
            })
        }

        return options;
    }, [allocationTypes]);

    
    const statusOptions = useMemo(() => {
        const options = [];


        const name = entityName.toLowerCase();

        if (statuses.includes(ConfigurationStatus.Disabled))
        {
            options.push({
                id: ConfigurationStatus.Disabled,
                name: 'Disabled',
                description: `The ${name} is disabled in the Source Data Verification system.`
            })
        }

        if (statuses.includes(ConfigurationStatus.ReadOnly))
        {
            options.push({
                id: ConfigurationStatus.ReadOnly,
                name: 'Read Only',
                description: `The ${name} is read only in the Source Data Verification system.`
            })
        }

        if (statuses.includes(ConfigurationStatus.Enabled))
        {
            options.push(
                {
                    id: ConfigurationStatus.Enabled,
                    name: 'Enabled',
                    description: `The ${name} is enabled in the Source Data Verification system.`
                })
        }

        return options;
    }, [statuses, entityName]);


    const loading = configurationLoading || configurationSourceDataAllocationsLoading || configurationSourceDatasLoading;
    const validating = configurationValidating || configurationSourceDataAllocationsValidating || configurationSourceDatasValidating;

    const initialData = useMemo(() => {
        return {
            configuration: configuration ?? contextQuery,
            allocations: configurationSourceDataAllocations ?? []
        } as IForm<TConfiguration>
        
    }, [configuration, configurationSourceDataAllocations, contextQuery]);

    const sourceDataOptions = useMemo(() => {
        return {
            list: configurationSourceDatas?.map(x => ({
                id: x.id,
                name: x.name,
                description: x.description
            })),
            map: configurationSourceDatas?.reduce<Record<number, ConfigurationSourceData>>(
                (a, b) => {
                    if (!b.id) {
                        return a;
                    }

                    a[b.id] = b;

                    return a;
                },
                {}
            ) ?? {}
        }
    }, [configurationSourceDatas])

    const formRef = useRef<FormManager<IForm<TConfiguration>, FormError>>(null);

    const navigate = useNavigate();

    const onCancel = useCallback(() => {
        if (returnUrl) {
            navigate(returnUrl);
        }
        else {
            navigate(`/${trialOid}/configuration`)
        }
    }, [navigate, trialOid, returnUrl])

    const save = useCallback(async (data?: TConfiguration, allocations?: ConfigurationSourceDataAllocation[], updates?: ParticipantSourceDataAllocationUpdate[]) => {
        await onSave(client, data, allocations, updates);

        onCancel();

        enqueueSnackbar(`${entityName} Configuration successfully saved!`, { variant: "success" });

        return {};
    }, [client, onCancel, enqueueSnackbar, entityName, onSave]);

    const submit = useCallback<IFormSubmit<IForm<TConfiguration>, FormError>>(async (formState, formActions) => {
        const response = await onCalculateAllocations(client, contextQuery, formState.value?.configuration, formState.value?.allocations);

        if (!response.updates || !response.updates.some(x => x.update)) {
            await save(formState.value?.configuration, formState.value?.allocations, response.updates);
        }
        else {
            setAllocationForm({
                data: formState.value?.configuration,
                allocations: formState.value?.allocations,
                participantSourceDataAllocations: response.updates
            })
        }

        return {};
    }, [client, save, setAllocationForm, onCalculateAllocations, contextQuery]);

    const submitFailed = useCallback<IFormSubmitFailed<IForm, FormError>>(async (formState, formActions) => {
        
        enqueueSnackbar(`An error occurred while attempting to save the ${entityName} Configuration!`, { variant: "error" });

        return {};
    }, [enqueueSnackbar, entityName]);

    const submitValidationFailed = useCallback<IFormSubmitValidationFailed<IForm, FormError>>(async (formState, formActions, validationException) => {
        enqueueSnackbar("The form contains invalid data, please correct the marked fields and try again!", { variant: "warning" });

        return formState.errors;
    }, [enqueueSnackbar]);

    
    const validate = useCallback<IFormValidate<IForm, FormError>>(async (formState, formActions) => {
        let errors: Record<string, FormError[]> = {};

        if (!formState?.value?.configuration?.status) {
            errors['configuration.status'] = [
                {
                    property: 'configuration.status',
                    message: 'Status must be provided!',
                    type: FormErrorType.Normal
                }
            ];
        }

        if (!formState?.value?.configuration?.allocationType) {
            errors['configuration.allocationType'] = [
                {
                    property: 'configuration.allocationType',
                    message: 'Allocation Type must be provided!',
                    type: FormErrorType.Normal
                }
            ];
        }
        else {
            switch (formState.value.configuration.allocationType) {
                case SourceDataAllocationType.Constant:
                    if (!formState?.value?.allocations ||
                        formState.value.allocations.length === 0 ||
                        !formState.value.allocations[0].configurationSourceDataId) {
                        errors['allocations[0].configurationSourceDataId'] = [
                            {
                                property: 'allocations[0].configurationSourceDataId',
                                message: 'Constant Allocation must be provided!',
                                type: FormErrorType.Normal
                            }
                        ];
                    }
                    break;
                case SourceDataAllocationType.FirstXParticipants:
                    if (!formState?.value?.allocations ||
                        formState.value.allocations.length === 0 ||
                        !formState.value.allocations[0].configurationSourceDataId) {
                        errors['allocations[0].configurationSourceDataId'] = [
                            {
                                property: 'allocations[0].configurationSourceDataId',
                                message: 'Constant Allocation must be provided!',
                                type: FormErrorType.Normal
                            }
                        ];
                    }

                    if (formState?.value?.allocations && formState.value.allocations.length > 1 ) {
                        for (let i = 1; i < formState.value.allocations.length; i++) {
                            if (!formState.value.allocations[i].configurationSourceDataId) {
                                errors[`allocations[${i}].configurationSourceDataId`] = [
                                    {
                                        property: `allocations[${i}].configurationSourceDataId`,
                                        message: `${ordinal(i)} Participant Allocation must be provided!`,
                                        type: FormErrorType.Normal
                                    }
                                ];
                            }
                        }
                    }

                    break;
                case SourceDataAllocationType.RepeatingCycle:
                    if (formState?.value?.allocations && formState.value.allocations.length > 0 ) {
                        for (let i = 0; i < formState.value.allocations.length; i++) {
                            if (!formState.value.allocations[i].configurationSourceDataId) {
                                errors[`allocations[${i}].configurationSourceDataId`] = [
                                    {
                                        property: `allocations[${i}].configurationSourceDataId`,
                                        message: `${ordinal(i + 1)} Participant Allocation must be provided!`,
                                        type: FormErrorType.Normal
                                    }
                                ];
                            }
                        }
                    }
                    break;
                case SourceDataAllocationType.FirstXParticipantsAndRepeatingCycle:
                    if (formState?.value?.allocations && formState.value.allocations.length > 1 ) {
                        for (let i = 1; i < formState.value.allocations.length; i++) {
                            if (!formState.value.allocations[i].configurationSourceDataId) {
                                errors[`allocations[${i}].configurationSourceDataId`] = [
                                    {
                                        property: `allocations[${i}].configurationSourceDataId`,
                                        message: `Participant Allocation must be provided!`,
                                        type: FormErrorType.Normal
                                    }
                                ];
                            }
                        }
                    }

                    break;
            }
        }

        errors = await onValidate(client, errors, formState);

        return errors;
    }, [onValidate, client, onValidate]);

    const onAllocationClose = useCallback(() => {
        setAllocationForm(undefined);
    }, [setAllocationForm]);

    const onAllocationChange = useCallback((_: any, __:any, selectedItem: any) => {
        if (!selectedItem) {
            formRef.current?.setFieldValue('allocations', [], true, true, true, true);
        }
        else if (selectedItem.id === SourceDataAllocationType.Constant) {
            formRef.current?.setFieldValue('allocations', [new ConfigurationSourceDataAllocation({ ...contextQuery, type: SourceDataAllocationType.Constant })], true, true, true, true);
        }
        else if (selectedItem.id === SourceDataAllocationType.FirstXParticipants) {
            formRef.current?.setFieldValue('allocations', [new ConfigurationSourceDataAllocation({ ...contextQuery, type: SourceDataAllocationType.Constant }), new ConfigurationSourceDataAllocation({ ...contextQuery, type: SourceDataAllocationType.FirstXParticipants })], true, true, true, true);
        }
        else if (selectedItem.id === SourceDataAllocationType.RepeatingCycle) {
            formRef.current?.setFieldValue('allocations', [new ConfigurationSourceDataAllocation({ ...contextQuery, type: SourceDataAllocationType.RepeatingCycle })], true, true, true, true);
            
        }
        else if (selectedItem.id === SourceDataAllocationType.FirstXParticipantsAndRepeatingCycle) {
            formRef.current?.setFieldValue('allocations', [new ConfigurationSourceDataAllocation({ ...contextQuery, type: SourceDataAllocationType.FirstXParticipants }), new ConfigurationSourceDataAllocation({ ...contextQuery, type: SourceDataAllocationType.RepeatingCycle })], true, true, true, true);
            
        }
        else if (selectedItem.id === SourceDataAllocationType.Inherit) {
            formRef.current?.setFieldValue('allocations', [], true, true, true, true);
        }
    }, [formRef, contextQuery]);

    return (
        <>
            <IsLoading name="configuration-context-template" loading={loading} validating={validating} />
            <Show show={!loading}>
                <Typography variant="h1" gutterBottom>
                    Configuration &mdash; {entityName}
                    {
                        title && (
                            <Box
                                sx={{
                                    fontSize: '2rem'
                                }}
                            >
                                {title}
                            </Box>
                        )
                    }
                </Typography>
                <ParticipantSourceDataAllocationSelectionDialog 
                    open={!!allocationForm} 
                    formData={allocationForm}
                    onClose={onAllocationClose}
                    onSave={save}
                    sourceDataOptions={sourceDataOptions.map}
                    institutions={institutions}
                    participants={participants}
                />
                <Form
                    initialValue={initialData}
                    debug={AppSettings.debug}
                    formRef={formRef}
                    onSubmit={submit}
                    onSubmitFailed={submitFailed}
                    onSubmitValidationFailed={submitValidationFailed}
                    onValidate={validate}
                    validateOn={ValidateOn.onChange}
                >
                    <Typography variant="h2" gutterBottom>
                        Details
                    </Typography>
                    <FormContainer>
                        {children}
                        <InputOnlyField
                            propertyPath="configuration.status"
                            label="Status"
                            helperText={`The status of the ${entityNameLowerCase} within the Source Data Verification system.`}
                            FormControlProps={{
                                margin: 'normal'
                            }}
                            component={Select}
                            options={statusOptions}
                        />
                    </FormContainer>
                    <Typography variant="h2" gutterBottom>
                        Messages
                    </Typography>
                    <FormContainer>
                        <InputOnlyField
                            propertyPath="configuration.alertSuccess"
                            label="Message - Success Styling"
                            helperText={`Enter a message that will be displayed in success styling (green) at the top of every page related to this ${entityNameLowerCase}.`}
                            margin="normal"
                            component={AlertMarkdown}
                            severity="success"
                        />
                        <InputOnlyField
                            propertyPath="configuration.alertInformation"
                            label="Message - Information Styling"
                            helperText={`Enter a message that will be displayed in information styling (blue) at the top of every page related to this ${entityNameLowerCase}.`}
                            margin="normal"
                            component={AlertMarkdown}
                            severity="info"
                        />
                        <InputOnlyField
                            propertyPath="configuration.alertWarning"
                            label="Message - Warning Styling"
                            helperText={`Enter a message that will be displayed in warning styling (yellow) at the top of every page related to this ${entityNameLowerCase}.`}
                            margin="normal"
                            component={AlertMarkdown}
                            severity="warning"
                        />
                        <InputOnlyField
                            propertyPath="configuration.alertError"
                            label="Message - Error Styling"
                            helperText={`Enter a message that will be displayed in error styling (red) at the top of every page related to this ${entityNameLowerCase}.`}
                            margin="normal"
                            component={AlertMarkdown}
                            severity="error"
                        />
                    </FormContainer>
                    <Typography variant="h2" gutterBottom>
                        Allocation
                    </Typography>
                    <FormContainer>
                        <InputOnlyField
                            propertyPath="configuration.allocationType"
                            label="Allocation Type"
                            helperText="Select how participant review level allocation is handled."
                            InputLabelProps={{
                                shrink: true
                            }}
                            FormControlProps={{
                                margin: 'normal'
                            }}
                            component={Select}
                            onChange={onAllocationChange}
                            options={allocationOptions}
                        />
                        <Condition
                            mode={ConditionMode.Show}
                            subscription={{ value: true }}
                            condition={constantAllocationCondition}
                            onConditionNotMet={onConditionNotMet}
                        >
                            <ConfigurationTrialConstant 
                                sourceDataOptions={sourceDataOptions.list} 
                                contextQuery={contextQuery} 
                            />
                        </Condition>
                        <Condition
                            mode={ConditionMode.Show}
                            subscription={{ value: true }}
                            condition={firstXAllocationCondition}
                            onConditionNotMet={onConditionNotMet}
                        >
                            <ConfigurationTrialFirstX 
                                sourceDataOptions={sourceDataOptions.list} 
                                contextQuery={contextQuery} 
                            />
                        </Condition>
                        <Condition
                            mode={ConditionMode.Show}
                            subscription={{ value: true }}
                            condition={repeatingAllocationCondition}
                            onConditionNotMet={onConditionNotMet}
                        >
                            <ConfigurationTrialRepeatingCycle 
                                sourceDataOptions={sourceDataOptions.list} 
                                contextQuery={contextQuery} 
                            />
                        </Condition>
                        <Condition
                            mode={ConditionMode.Show}
                            subscription={{ value: true }}
                            condition={firstXAndrepeatingAllocationCondition}
                            onConditionNotMet={onConditionNotMet}
                        >
                            <ConfigurationTrialFirstXAndRepeatingCycle 
                                sourceDataOptions={sourceDataOptions.list} 
                                contextQuery={contextQuery} 
                            />
                        </Condition>
                    </FormContainer>
                    <FormButtons>
                        <FormSubmitButton
                            variant="contained"
                            color="primary"
                        >
                            Save
                        </FormSubmitButton>
                        <Button
                            variant="contained"
                            color="inherit"
                            onClick={onCancel}
                        >
                            Cancel
                        </Button>
                    </FormButtons>
                </Form>
            </Show>
        </>
    )
};

export default ConfigurationContextTemplate;